import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BillingAdaptor } from '../adaptors/billing.adaptor';
import { BillingStatus } from '../models/billing.interface';
import { AuthService } from './auth/auth.service';

/**
 * The workshop's billing status, shared by the banner in the layout and the Billing page so the
 * API is asked once, not once per screen.
 *
 * `billing` is null in two cases that the screens treat the same way, and do not need to tell
 * apart: the workshop is not billed (an older customer), or billing is switched off for this
 * environment (the API answers 404). Either way: show no billing UI. A real failure (server down,
 * no network) sets `error` and leaves the last known status in place, so a brief blip does not
 * make a trial banner disappear.
 */
@Injectable({ providedIn: 'root' })
export class BillingStateService {
  private adaptor = inject(BillingAdaptor);
  private authService = inject(AuthService);

  readonly billing = signal<BillingStatus | null>(null);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly error = signal<string | null>(null);

  async refresh(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const tenantId = await this.authService.getTenantId();
      const response = await firstValueFrom(this.adaptor.getStatus(tenantId));
      this.billing.set(response.billing);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.billing.set(null);
      } else {
        this.error.set("We couldn't load your billing details. Please try again.");
      }
    } finally {
      this.loading.set(false);
      this.loaded.set(true);
    }
  }
}
